import { FormRow } from "@/features/forms/components/FormRow";
import { useForms } from "@/features/forms/hooks/formQueries";
import { useFormPreviewOverlay } from "@/features/forms/hooks/useFormPreviewOverlay";
import { EmptyState } from "@/shared/components/ui/EmptyState";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { ItemList } from "@/shared/components/ui/ItemList";
import { RowSkeleton } from "@/shared/components/ui/RowSkeleton";
import { Section } from "@/shared/components/ui/Section";
import { SectionAction } from "@/shared/components/ui/SectionAction";
import type { Service } from "../types/serviceTypes";

interface ServiceFormsSectionProps {
  formIds: string[];
  attachedForms?: Service["forms"];
  onAttach?: () => void;
  onRemove?: (formId: string) => void;
  disabled?: boolean;
}

export function ServiceFormsSection({ formIds, attachedForms = [], onAttach, onRemove, disabled }: ServiceFormsSectionProps) {
  const query = useForms();
  const { openPreview } = useFormPreviewOverlay();
  const forms = formIds.map((id) => {
    const live = query.data?.find((form) => form.id === id);
    const attached = attachedForms.find((form) => form.formId === id);
    return { id, name: live?.name ?? attached?.name ?? "Unavailable form", version: live?.version ?? attached?.version, available: !!live };
  });

  return (
    <Section title="Forms" action={onAttach && <SectionAction disabled={disabled} onClick={onAttach}>Attach forms</SectionAction>}>
      {query.isLoading ? <RowSkeleton /> : query.error ? (
        <ErrorState message={query.error.message} onRetry={() => void query.refetch()} />
      ) : !forms.length ? (
        <EmptyState message="No forms attached to this service." />
      ) : (
        <ItemList items={forms} renderItem={(form) => <FormRow
          form={form}
          caption={form.available ? undefined : "This form is no longer available for new bookings. Saved appointments retain their previews."}
          onPreview={disabled || !form.available ? undefined : () => openPreview(form.id)}
          onRemove={!disabled && onRemove ? () => onRemove(form.id) : undefined}
        />} />
      )}
    </Section>
  );
}
